/**
 * Models
 */
export const DietAveragePricesToModel = (prices: IDietAveragePrices): IDietAveragePricesModel => {
	const {
		average_cost,
		average_cost_gross,
		average_margin,
		average_margin_gross,
		average_margin_amount,
		average_margin_amount_gross,
	} = prices

	return {
		averageCost: average_cost ? PriceToShowNumber(average_cost) : null,
		averageCostGross: average_cost_gross ? PriceToShowNumber(average_cost_gross) : null,
		averageMargin: average_margin ?? null,
		averageMarginGross: average_margin_gross ?? null,
		averageMarginAmount: average_margin_amount ? PriceToShowNumber(average_margin_amount) : null,
		averageMarginAmountGross: average_margin_amount_gross ? PriceToShowNumber(average_margin_amount_gross) : null,
	}
}

export const DietVariantPricesToModel = (json: IDietVariantPrices): IDietVariantPricesModel => {
	const { price, cost, cost_gross, margin, margin_gross, margin_amount, margin_amount_gross } = json

	return {
		price: price ? PriceToShowNumber(price) : null,
		cost: cost ? PriceToShowNumber(cost) : null,
		costGross: cost_gross ? PriceToShowNumber(cost_gross) : null,
		margin: margin ?? null,
		marginGross: margin_gross ?? null,
		marginAmount: margin_amount ? PriceToShowNumber(margin_amount) : null,
		marginAmountGross: margin_amount_gross ? PriceToShowNumber(margin_amount_gross) : null,
	}
}

/**
 * Forms
 */
const AVAILABLE_TIMES = Object.values(EMealType)
const AVAILABLE_SUBKINDS = Object.values(EMealSubKind)

const buildKindSizes = (
	sizes: IDietMealSizeModel[],
	prices: IMealPriceTierModel[],
	level: number,
	time: EMealType,
	subkind: EMealSubKind,
): IPriceListSize[] =>
	sizes.map(size => {
		const subkindValue = subkind === EMealSubKind.DEFAULT ? null : subkind
		const target = prices.find(
			p =>
				p.mealPriceTier === level &&
				p.mealType === time &&
				p.mealSize?.id === size.id &&
				p.mealSubkind === subkindValue,
		)
		return {
			id: size.id,
			label: size.name,
			price: target?.price ? PriceToShowNumber(target.price) : null,
			points: null,
		}
	})

export const DietPriceTierToForm = (prices: IMealPriceTierModel[], sizes: IDietMealSizeModel[]): IPriceListTier[] => {
	const array: IPriceListTier[] = []

	AVAILABLE_PRICE_TIERS.forEach(level => {
		const levelObject: IPriceListTier = {
			level,
			times: [],
		}

		AVAILABLE_TIMES.forEach(time => {
			const obj: IPriceListTime = {
				time,
				kinds: [],
			}

			AVAILABLE_SUBKINDS.forEach(subkind => {
				obj.kinds.push({
					subkind,
					sizes: buildKindSizes(sizes, prices, level, time, subkind),
				})
			})

			levelObject.times.push(obj)
		})

		array.push(levelObject)
	})

	return array
}

interface IBuildSizePayloadOptions {
	size: IPriceListSize
	sIndex: number
	kIndex: number
	tIndex: number
	levelIndex: number
	values: Record<string, any>
	time: IPriceListTime
	kind: IPriceListKinds
	level: IPriceListTier
}

const buildSizePayload = ({
	size,
	sIndex,
	kIndex,
	tIndex,
	levelIndex,
	values,
	time,
	kind,
	level,
}: IBuildSizePayloadOptions): IDietPriceTierPayload => {
	const price = values[`price[${levelIndex}][${tIndex}][${kIndex}][${sIndex}]`]
	const validPrice = price && !Number.isNaN(Number(price)) ? price : null
	return {
		meal_type: time.time,
		meal_kind: EMealKind.MEAL,
		meal_subkind: kind.subkind === EMealSubKind.DEFAULT ? null : kind.subkind,
		meal_price_tier: level.level,
		meal_size_id: size.id,
		price: validPrice ? PriceToPayload(validPrice) : null,
		is_active: true,
	}
}

const buildKindPayloads = (
	kind: IPriceListKinds,
	kIndex: number,
	tIndex: number,
	levelIndex: number,
	values: Record<string, any>,
	time: IPriceListTime,
	level: IPriceListTier,
): IDietPriceTierPayload[] =>
	kind.sizes.map((size, sIndex) =>
		buildSizePayload({ size, sIndex, kIndex, tIndex, levelIndex, values, time, kind, level }),
	)

/**
 * Paylods
 */
export const DietPricesToPayload = (
	event: IFormSubmitEvent<IDietPricesForm>,
	options: IDietMealVariantModel[],
	model_type: 'addon' | 'dietary_meal_variant',
): IDietPricesPayload[] => {
	const values = event.values

	return options.map(option => {
		const price = values[`price[${option.id}]`]
		const validPrice = price && !Number.isNaN(Number(price)) ? price : null

		return {
			model_type,
			model_id: option.id,
			price: validPrice ? PriceToPayload(validPrice) : null,
			points: validPrice ? Number(validPrice) : null,
		}
	})
}

export const DietPriceTierToPayload = (
	event: IFormSubmitEvent<any>,
	prices: IPriceListTier[],
): IDietPriceTierPayload[] => {
	const values = event.values

	const array: IDietPriceTierPayload[] = []
	prices.forEach((level, levelIndex) => {
		level.times.forEach((time, tIndex) => {
			time.kinds.forEach((kind, kIndex) => {
				array.push(...buildKindPayloads(kind, kIndex, tIndex, levelIndex, values, time, level))
			})
		})
	})

	return array.filter(item => item.price !== null)
}

export const DietAveragePricesToPayload = (prices: IDietAveragePricesForm): IDietAveragePricesPayload => {
	const {
		averageCost,
		averageCostGross,
		averageMargin,
		averageMarginGross,
		averageMarginAmount,
		averageMarginAmountGross,
	} = prices

	return {
		average_cost: averageCost !== null && averageCost !== undefined ? PriceToPayload(averageCost) : null,
		average_cost_gross: averageCostGross ? PriceToPayload(averageCostGross) : null,
		average_margin: averageMargin ?? null,
		average_margin_gross: averageMarginGross ?? null,
		average_margin_amount: averageMarginAmount ? PriceToPayload(averageMarginAmount) : null,
		average_margin_amount_gross: averageMarginAmountGross ? PriceToPayload(averageMarginAmountGross) : null,
	}
}

export const DietPriceVariantToPayload = (variant: IDietSharedMealVariantForm): IDietPriceVariantPayload => {
	const { costNet, costGross, marginNet, marginGross, marginAmountNet, marginAmountGross } = variant.prices

	return {
		cost: costNet ? PriceToPayload(costNet) : null,
		cost_gross: costGross ? PriceToPayload(costGross) : null,
		margin: marginNet ?? null,
		margin_gross: marginGross ?? null,
		margin_amount: marginAmountNet ? PriceToPayload(marginAmountNet) : null,
		margin_amount_gross: marginAmountGross ? PriceToPayload(marginAmountGross) : null,
	}
}
